import { useCallback, useEffect, useState } from 'react'
import apiClient from '../../services/apiClient'
import { useAuthStore } from '../../store/authStore'
import { UserRole } from '../../types/auth'
import { toast } from 'react-toastify'
import {
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import {
  isExcalidrawScene,
  isLegacyFreehandScene,
} from './FreehandWhiteboard'
import type { WhiteboardEditRequest } from '../admin/DocumentViewer'

export type MaterialWhiteboard = {
  id: number
  materialId: number
  courseId: number
  title: string
  diagramId: string
  pngUrl: string
  xmlUrl: string
  isPublished: number | boolean
  createdAt?: string
}

type Props = {
  courseId: number
  materialId: number
  refreshKey?: number
  /** Admin: open whiteboard for re-edit */
  onEditWhiteboard?: (req: WhiteboardEditRequest) => void
  /** Default collapsed to keep document frame large (option A) */
  defaultCollapsed?: boolean
}

const BROKEN_PNG =
  'data:image/svg+xml,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="72"><rect fill="#f1f5f9" width="100%" height="100%"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="#94a3b8" font-size="10" font-family="sans-serif">Không tải được ảnh</text></svg>`
  )

export default function MaterialWhiteboardList({
  courseId,
  materialId,
  refreshKey = 0,
  onEditWhiteboard,
  defaultCollapsed = true,
}: Props) {
  const user = useAuthStore((s) => s.user)
  const isAdmin = Boolean(
    user?.roles?.includes(UserRole.ADMIN) ||
      (user?.roles as string[] | undefined)?.includes('SUPER_ADMIN')
  )
  const [items, setItems] = useState<MaterialWhiteboard[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [preview, setPreview] = useState<MaterialWhiteboard | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [collapsed, setCollapsed] = useState(defaultCollapsed)

  const load = useCallback(async () => {
    if (!courseId || !materialId) return
    setLoading(true)
    setLoadError(null)
    try {
      if (isAdmin) {
        const res = await apiClient.get(`/admin/materials/${materialId}/whiteboards`)
        setItems(Array.isArray(res.data?.data) ? res.data.data : [])
      } else {
        const res = await apiClient.get(
          `/student/courses/${courseId}/materials/${materialId}/whiteboards`
        )
        setItems(Array.isArray(res.data?.data) ? res.data.data : [])
      }
    } catch (e: any) {
      const message = e?.response?.data?.message || 'Không tải được bảng minh họa'
      setLoadError(message)
      setItems([])
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }, [courseId, materialId, isAdmin])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  useEffect(() => {
    setCollapsed(defaultCollapsed)
  }, [materialId, defaultCollapsed])

  // Reset fullscreen khi đổi preview
  useEffect(() => {
    if (preview) setIsFullscreen(false)
  }, [preview])

  const handleDelete = async (id: number) => {
    if (!window.confirm('Xóa bảng trắng này?')) return
    try {
      await apiClient.delete(`/admin/whiteboards/${id}`)
      toast.success('Đã xóa')
      void load()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Xóa thất bại')
    }
  }

  const handleEdit = async (wb: MaterialWhiteboard) => {
    if (!onEditWhiteboard) return
    setEditingId(wb.id)
    try {
      const res = await apiClient.get('/admin/uploads/diagram-xml', {
        params: { url: wb.xmlUrl },
        responseType: 'text',
        transformResponse: [(data) => data],
      })
      const raw = typeof res.data === 'string' ? res.data : String(res.data ?? '')

      if (isLegacyFreehandScene(raw)) {
        toast.info(
          'Bảng này lưu bằng bút cũ — chỉ xem PNG được. Tạo bảng mới bằng Excalidraw để sửa.'
        )
        return
      }
      if (!isExcalidrawScene(raw)) {
        toast.error('Không đọc được nội dung bảng trắng để sửa')
        return
      }

      onEditWhiteboard({
        whiteboardId: wb.id,
        materialId: wb.materialId,
        title: wb.title,
        sceneJson: raw,
        diagramId: wb.diagramId,
      })
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Không tải được bảng để sửa')
    } finally {
      setEditingId(null)
    }
  }

  const countLabel =
    loading && items.length === 0
      ? '…'
      : loadError && items.length === 0
        ? 'lỗi'
        : `${items.length} bảng`

  const closePreview = () => {
    setPreview(null)
    setIsFullscreen(false)
  }

  const previewModal = preview ? (
    <div
      className="fixed inset-0 z-[90] bg-black/75 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={closePreview}
    >
      <div
        className={`bg-white flex flex-col transition-all duration-200 ${
          isFullscreen
            ? 'w-screen h-screen max-w-none rounded-none'
            : 'rounded-xl max-w-4xl w-full max-h-[90vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 shrink-0">
          <h3 className="font-semibold text-m2-on-surface truncate min-w-0">{preview.title}</h3>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              title={isFullscreen ? 'Thu gọn' : 'Mở toàn màn hình'}
              className="p-1.5 rounded hover:bg-slate-100 text-m2-on-surface-variant hover:text-m2-primary transition-colors"
              onClick={() => setIsFullscreen((v) => !v)}
            >
              {isFullscreen
                ? <ArrowsPointingInIcon className="h-5 w-5" />
                : <ArrowsPointingOutIcon className="h-5 w-5" />}
            </button>
            <button
              type="button"
              title="Đóng"
              className="p-1.5 rounded hover:bg-slate-100 text-m2-on-surface-variant hover:text-red-500 transition-colors"
              onClick={closePreview}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
        {/* Image */}
        <div className="flex-1 overflow-auto p-4">
          <img
            src={preview.pngUrl}
            alt={preview.title}
            className="w-full h-auto rounded border block"
            onError={(e) => {
              e.currentTarget.onerror = null
              e.currentTarget.src = BROKEN_PNG
            }}
          />
        </div>
      </div>
    </div>
  ) : null

  return (
    <div className="mt-2 border border-m2-outline-variant rounded-lg overflow-hidden bg-m2-surface-low">
      <button
        type="button"
        className="w-full px-3 py-2 flex items-center justify-between gap-2 text-left hover:bg-white/50 transition-colors"
        onClick={() => setCollapsed((v) => !v)}
        aria-expanded={!collapsed}
      >
        <span className="text-sm font-semibold text-m2-on-surface">Bảng minh họa</span>
        <span className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] text-m2-on-surface-variant">{countLabel}</span>
          <span className="text-xs text-m2-on-surface-variant" aria-hidden>
            {collapsed ? '▾' : '▴'}
          </span>
        </span>
      </button>

      {!collapsed && (
        <>
          {loading && items.length === 0 && (
            <div className="px-3 py-2 text-xs text-m2-on-surface-variant border-t border-m2-outline-variant">
              Đang tải bảng minh họa…
            </div>
          )}
          {loadError && items.length === 0 && (
            <div className="px-3 py-2 text-xs text-red-700 border-t border-m2-outline-variant bg-red-50">
              {loadError}
            </div>
          )}
          {!loading && !loadError && items.length === 0 && (
            <div className="px-3 py-3 text-xs text-m2-on-surface-variant border-t border-m2-outline-variant border-dashed">
              Chưa có bảng minh họa cho tài liệu này
            </div>
          )}
          {items.length > 0 && (
            <ul className="divide-y divide-m2-outline-variant max-h-48 overflow-y-auto border-t border-m2-outline-variant">
              {items.map((wb) => (
                <li key={wb.id} className="flex items-center gap-2 px-3 py-2 hover:bg-white/60">
                  <button
                    type="button"
                    className="flex items-center gap-2 min-w-0 flex-1 text-left"
                    onClick={() => setPreview(wb)}
                  >
                    <img
                      src={wb.pngUrl}
                      alt=""
                      className="w-12 h-9 object-cover rounded border border-m2-outline-variant shrink-0 bg-white"
                      onError={(e) => {
                        e.currentTarget.onerror = null
                        e.currentTarget.src = BROKEN_PNG
                      }}
                    />
                    <span className="text-sm text-m2-on-surface truncate">{wb.title}</span>
                  </button>
                  {isAdmin && onEditWhiteboard && (
                    <button
                      type="button"
                      className="text-xs text-m2-primary hover:underline shrink-0 disabled:opacity-50"
                      disabled={editingId === wb.id}
                      onClick={() => void handleEdit(wb)}
                    >
                      {editingId === wb.id ? 'Đang tải…' : 'Sửa'}
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      className="text-xs text-red-600 hover:underline shrink-0"
                      onClick={() => void handleDelete(wb.id)}
                    >
                      Xóa
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {previewModal}
    </div>
  )
}
